import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  IdCard, Download, Printer, RefreshCw, Loader2, ShieldCheck, Info,
} from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, Callout,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import { IdCardFront, IdCardBack } from "./IdCardFaces";
import { fetchMyCard, fetchStudentCard, errorText } from "./identityCardApi";

/**
 * IdentityCardSection — the digital ID card, with the two things people do with
 * one: save it, and print it.
 *
 * ⚠️ THE PAPER LAYOUT IS A SECOND, ALWAYS-RENDERED COPY, and that is what makes
 * both outputs work from one definition. It sits at `left: -9999px` on screen —
 * RENDERED, so html2canvas can photograph it, just not visible — and
 * `print:tw-static` returns it to the flow on paper, where `left` no longer
 * applies because a static element ignores it. `tw-hidden` would have been the
 * obvious choice and breaks the download: html2canvas cannot capture
 * `display: none`.
 *
 * ⚠️ Both faces print at EXACT millimetres, not at a screen size scaled by the
 * printer. A badge printer and a laminator pouch are cut for 53.98 × 85.60 mm;
 * a card that is 4% out is a card that does not fit the sleeve.
 *
 * ⚠️ `useCORS` belongs on html2canvas, NOT on the live `<img>` tags. The photo
 * and the logo are presigned R2 URLs; putting `crossOrigin="anonymous"` on the
 * real images would make them fail to LOAD outright wherever the bucket does not
 * send `Access-Control-Allow-Origin`, so the card would render with no photo and
 * no logo on screen — a permanent, visible fault traded for a possible download.
 * html2canvas sets it on its own cloned copies instead, so the worst case is a
 * tainted canvas at capture time. `toBlob` throws on that, which is caught and
 * reported with the Print route as the fallback, rather than left as a button
 * that does nothing.
 */

/** ID-1 / CR80 in millimetres — the physical card, not a screen approximation. */
const PRINT_CARD_WIDTH_MM = "53.98mm";

/** Card width on screen. Large enough to read every field without zooming. */
const SCREEN_CARD_WIDTH = "288px";

const IdentityCardSection = ({ studentId = null, title = "Digital ID Card" }) => {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // A 409 is not a failure to retry — the student simply has no login yet, so
  // there is no identity to key a credential on. It gets a plain explanation
  // rather than a red box and a "Try again" that can never succeed.
  const [unavailable, setUnavailable] = useState("");
  const [notice, setNotice] = useState("");
  const [face, setFace] = useState("front");
  const [busy, setBusy] = useState("");
  // Adds `print-sheet` only for the duration of a print — see `print()`.
  const [printing, setPrinting] = useState(false);
  const paperRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    setUnavailable("");
    const request = studentId ? fetchStudentCard(studentId) : fetchMyCard();
    return request
      .then(({ card: c }) => setCard(c))
      .catch((err) => {
        const message = errorText(err, "Failed to build the identity card.");
        if (err?.response?.status === 409) setUnavailable(message);
        else setError(message);
      })
      .finally(() => setLoading(false));
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  const download = async () => {
    if (!paperRef.current) return;
    setBusy("download");
    setNotice("");
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(paperRef.current, {
        // 3× so the PNG is print-resolution rather than a screenshot: at the
        // card's physical size this lands around 300dpi.
        scale: 3,
        useCORS: true,
        backgroundColor: null,
        logging: false,
      });
      const blob = await new Promise((resolve, reject) => {
        try {
          canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Empty image"))), "image/png");
        } catch (e) {
          // A tainted canvas — a cross-origin image the CORS headers did not cover.
          reject(e);
        }
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(card?.number || "identity-card").replace(/[^\w-]/g, "")}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoked on the next tick — revoking synchronously can beat the download.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setNotice("Card image downloaded.");
    } catch (err) {
      setError("The card image could not be generated. Use Print and choose “Save as PDF” instead.");
    } finally {
      setBusy("");
    }
  };

  /**
   * ⚠️ `print-sheet` is attached only WHILE printing, never permanently.
   *
   * That class hides `body *` and shows only its own subtree, so a card
   * carrying it all the time would hijack every Ctrl+P on this page — someone
   * printing their profile for a form would silently get an ID card instead.
   * The flag adds it, the effect below prints once React has committed it, and
   * it comes straight back off.
   */
  const print = () => {
    setNotice("");
    setPrinting(true);
  };

  useEffect(() => {
    if (!printing) return undefined;

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setPrinting(false);
      setBusy("");
    };

    setBusy("print");
    // `afterprint` is the reliable signal where it fires; the timeout is for the
    // browsers that never do, so the page cannot get stuck in print layout.
    window.addEventListener("afterprint", finish, { once: true });
    const bail = setTimeout(finish, 60000);

    // A frame, so the class is painted before the dialog freezes the thread.
    const raf = requestAnimationFrame(() => {
      window.print();
      // Desktop browsers block here until the dialog closes, which makes this
      // the normal exit; `afterprint` covers the ones that do not.
      finish();
    });

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(bail);
      window.removeEventListener("afterprint", finish);
    };
  }, [printing]);

  if (loading) {
    return (
      <AppCard>
        <AppCardContent>
          <Shimmer height={220} />
        </AppCardContent>
      </AppCard>
    );
  }

  if ((error || unavailable) && !card) {
    return (
      <AppCard>
        <AppCardHeader>
          <AppCardTitle className="[&_svg]:tw-text-primary">
            <IdCard size={18} /> {title}
          </AppCardTitle>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-3">
          <Callout tone={unavailable ? "info" : "danger"} role={unavailable ? "status" : "alert"}>
            {unavailable || error}
          </Callout>
          {!unavailable && (
            <div>
              <AppButton variant="outline" size="sm" onClick={load}>
                <RefreshCw size={14} /> Try again
              </AppButton>
            </div>
          )}
        </AppCardContent>
      </AppCard>
    );
  }

  if (!card) return null;

  const inactive = card.holder?.status === "inactive";

  return (
    <AppCard overflowVisible className={printing ? "print-sheet" : ""}>
      <AppCardHeader className="no-print tw-flex-row tw-items-start tw-justify-between tw-gap-3">
        <div className="tw-min-w-0">
          <AppCardTitle className="[&_svg]:tw-text-primary">
            <IdCard size={18} /> {title}
          </AppCardTitle>
          <p className="tw-m-0 tw-mt-1 tw-text-[0.8rem] tw-leading-relaxed tw-text-content-muted">
            Your institute identity card, branded and ready to print at standard card size.
          </p>
        </div>
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          <AppButton variant="outline" size="sm" onClick={download} disabled={!!busy}>
            {busy === "download"
              ? <><Loader2 size={14} className="tw-animate-spin" /> Preparing…</>
              : <><Download size={14} /> Download PNG</>}
          </AppButton>
          <AppButton variant="outline" size="sm" onClick={print} disabled={!!busy}>
            <Printer size={14} /> Print
          </AppButton>
        </div>
      </AppCardHeader>

      <AppCardContent className="no-print tw-flex tw-flex-col tw-gap-5 min-[760px]:tw-flex-row min-[760px]:tw-items-start">
        {/* The interactive card. Clicking it turns it over, which is what people
            try first — the button beneath is for anyone who does not. */}
        <div className="tw-flex tw-shrink-0 tw-flex-col tw-items-center tw-gap-3">
          <button
            type="button"
            onClick={() => setFace((f) => (f === "front" ? "back" : "front"))}
            aria-label={face === "front" ? "Show the back of the card" : "Show the front of the card"}
            className="tw-cursor-pointer tw-rounded-md tw-border-0 tw-bg-transparent tw-p-0 tw-focus-ring"
          >
            {face === "front"
              ? <IdCardFront card={card} unit={SCREEN_CARD_WIDTH} />
              : <IdCardBack card={card} unit={SCREEN_CARD_WIDTH} />}
          </button>
          <div className="tw-flex tw-gap-1" role="group" aria-label="Card side">
            {["front", "back"].map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFace(f)}
                aria-pressed={face === f}
                className={
                  "tw-cursor-pointer tw-rounded-full tw-border tw-border-solid tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-capitalize tw-transition-colors " +
                  (face === f
                    ? "tw-border-primary tw-bg-primary/10 tw-text-primary"
                    : "tw-border-line-soft tw-bg-surface-raised tw-text-content-muted hover:tw-text-content")
                }
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="tw-min-w-0 tw-flex-1 tw-flex tw-flex-col tw-gap-3">
          {notice && <Callout tone="success" role="status">{notice}</Callout>}
          {error && <Callout tone="danger" role="alert">{error}</Callout>}

          {inactive && (
            <Callout tone="warning">
              <span>
                <strong>This account is inactive.</strong> The card will verify as
                not currently valid until the account is reactivated.
              </span>
            </Callout>
          )}

          <dl className="tw-m-0 tw-grid tw-gap-x-6 tw-gap-y-3 min-[420px]:tw-grid-cols-2">
            <Detail label="Card number" value={card.number} mono />
            <Detail label="Issued to" value={card.holder?.name} />
            <Detail label="Role" value={card.holder?.roleLabel} />
            <Detail label="Institute" value={card.institute?.name} />
            <Detail label="Valid until" value={fmtDate(card.validUntil)} />
            <Detail
              label="Status"
              value={inactive ? "Inactive" : "Active"}
              tone={inactive ? "tw-text-warning" : "tw-text-success"}
            />
          </dl>

          <div className="tw-flex tw-gap-2 tw-rounded-sm tw-border tw-border-solid tw-border-line-soft tw-bg-surface-sunken tw-p-3">
            <ShieldCheck size={15} className="tw-mt-0.5 tw-shrink-0 tw-text-primary" />
            <p className="tw-m-0 tw-text-xs tw-leading-relaxed tw-text-content-muted">
              The QR on the back opens a public page that confirms this card is genuine and
              that you are still with the institute. It shows your name, role and photo, and
              nothing else — no contact details, no course or fee information.
            </p>
          </div>

          <div className="tw-flex tw-gap-2">
            <Info size={15} className="tw-mt-0.5 tw-shrink-0 tw-text-content-subtle" />
            <p className="tw-m-0 tw-text-xs tw-leading-relaxed tw-text-content-subtle">
              Print produces one sheet with both sides at exact card size
              (53.98 × 85.60 mm), so it can be cut and laminated, or sent to a badge printer.
            </p>
          </div>
        </div>
      </AppCardContent>

      {/* ── The paper layout ──────────────────────────────────────────────────
          Rendered always, positioned off-screen, and returned to the flow for
          print by `print:tw-static` — see the component header for why this is
          not `tw-hidden`. */}
      <div
        ref={paperRef}
        aria-hidden="true"
        className="tw-absolute tw-left-[-9999px] tw-top-0 tw-bg-surface-raised print:tw-static print:tw-bg-transparent [print-color-adjust:exact] [-webkit-print-color-adjust:exact]"
      >
        <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-[8mm] tw-p-[10mm]">
          <CutGuide>
            <IdCardFront card={card} unit={PRINT_CARD_WIDTH_MM} />
          </CutGuide>
          <CutGuide>
            <IdCardBack card={card} unit={PRINT_CARD_WIDTH_MM} />
          </CutGuide>
        </div>
      </div>
    </AppCard>
  );
};

/**
 * A dashed bleed frame around one face.
 *
 * Cards are cut by hand far more often than they are die-cut, and a face
 * printed with no surrounding guide gets trimmed into its own border. The
 * frame sits 2mm out and is not part of the card.
 */
const CutGuide = ({ children }) => (
  <div className="tw-border tw-border-dashed tw-border-line tw-p-[2mm]">{children}</div>
);

const Detail = ({ label, value, mono = false, tone = "tw-text-content" }) => (
  <div className="tw-min-w-0">
    <dt className="tw-text-[0.7rem] tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle">
      {label}
    </dt>
    <dd className={`tw-m-0 tw-mt-0.5 tw-truncate tw-text-[0.875rem] ${mono ? "tw-font-mono" : ""} ${tone}`}>
      {value || "—"}
    </dd>
  </div>
);

const fmtDate = (d) => (d
  ? new Date(d).toLocaleDateString("en-IN", {
    year: "numeric", month: "short", day: "numeric", timeZone: "Asia/Kolkata",
  })
  : "—");

export default IdentityCardSection;
